import React from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title, DoughnutController } from "chart.js";
import { Box, Typography, CircularProgress, useTheme } from "@mui/material";

ChartJS.register(ArcElement, Tooltip, Legend, Title, DoughnutController);

interface AgingAnalysisData {
  data: {
    range: string;
    count: number;
  }[];
}

interface AgingAnalysisPieChartProps {
  agingData?: AgingAnalysisData;
  isLoading?: boolean;
  error?: any;
  showTitle?: boolean;
}

const AgingAnalysisPieChart: React.FC<AgingAnalysisPieChartProps> = ({ 
  agingData,
  isLoading = false,
  error = null,
  showTitle = true
}) => {
  const theme = useTheme();

  const createChartData = () => {
    if (!agingData?.data) {
      return {
        labels: [],
        datasets: [{
          data: [],
          backgroundColor: [],
          borderColor: [],
          borderWidth: 1
        }]
      };
    }

    // Map the ranges to more readable labels
    const rangeLabels = {
      "0_30_days": "0-30 Days",
      "31_60_days": "31-60 Days",
      "61_plus_days": "61+ Days"
    };

    // Colors for different ranges
    const rangeColors = {
      "0_30_days": theme.palette.success.main,
      "31_60_days": theme.palette.warning.main,
      "61_plus_days": theme.palette.error.main
    };

    const data = agingData.data.map((item) => ({
      ...item,
      label: rangeLabels[item.range as keyof typeof rangeLabels] || item.range,
      color: rangeColors[item.range as keyof typeof rangeColors] || theme.palette.grey[500]
    }));

    return {
      labels: data.map((item) => item.label),
      datasets: [{
        data: data.map((item) => item.count),
        backgroundColor: data.map((item) => item.color),
        borderColor: theme.palette.background.paper,
        borderWidth: 1
      }]
    };
  };

  const chartData = createChartData();

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <Typography color="error">Failed to load aging analysis data</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%" }}>
      {showTitle && (
        <Typography variant="h6" align="center" sx={{ mb: 2 }}>Aging Analysis of Overdues</Typography>
      )}
      <Box sx={{ height: showTitle ? "calc(100% - 50px)" : "100%" }}>
        <Pie 
          data={chartData} 
          options={{ 
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  usePointStyle: true,
                  padding: 15,
                  color: theme.palette.text.primary,
                  font: {
                    weight: 'bold'
                  },
                  boxWidth: 10
                }
              },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                bodyFont: {
                  size: 14
                },
                callbacks: {
                  label: function (context) {
                    const label = context.label || '';
                    const value = context.raw as number;
                    const dataset = context.dataset;
                    const total = (dataset.data as number[]).reduce((acc, data) => acc + data, 0);
                    const percentage = Math.round((value / total) * 100);
                    return `${label}: ${percentage}% (Count: ${value})`;
                  }
                }
              }
            },
            animation: {
              animateScale: true,
              animateRotate: true
            }
          }} 
        />
      </Box>
    </Box>
  );
};

export default AgingAnalysisPieChart;
